import React from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Navbar,
  Nav,
  Badge,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  FileText,
  GitBranch,
  Users,
  Clock,
  CheckCircle2,
  ArrowRight,
  Cloud,
  Calendar,
  BellRing,
  FolderKanban,
  MessageSquareLock,
  History,
  CalendarCheck,
  Presentation,
  Layers,
  Brain,
  Cpu,
} from "lucide-react";

import styles from "./NovianCoreLandingPage.module.scss";

/**
 * Landing page de NovianCore
 *
 * Versión visualmente mejorada:
 * - Hero con gradiente y efecto “glass”
 * - Mock del timeline más elegante
 * - Sección de métricas / confianza
 * - Secciones con mejor ritmo visual
 */
const NovianCoreLandingPage = () => {
  return (
    <div className={styles.landingRoot}>
      {/* CABECERA FIJA / STICKY */}
      <header className={styles.header}>
        <Navbar expand="lg" className={styles.navbar} sticky="top">
          <Container>
            {/* Logo y nombre del producto */}
            <Navbar.Brand as={Link} to="/dashboard">
              <div className="d-flex align-items-center gap-2">
                <div className={styles.logoWrapper}>
                  <img
                    src={require("../../assets/images/brand/logo3.png")}
                    alt="NovianCore"
                    className={styles.logo}
                  />
                </div>
                <div className="d-flex flex-column">
                  <span className={styles.brandText}>NovianCore</span>
                  <span className={styles.brandTagline}>
                    El nuevo núcleo de la gestión legal
                  </span>
                </div>
              </div>
            </Navbar.Brand>

            <Navbar.Toggle aria-controls="noviancore-navbar" />

            <Navbar.Collapse id="noviancore-navbar">
              <Nav className="ms-auto align-items-lg-center">
                <Nav.Link href="#caracteristicas">Características</Nav.Link>
                <Nav.Link href="#flujo">Flujo</Nav.Link>
                <Nav.Link href="#seguridad">Seguridad</Nav.Link>
                <Nav.Link href="#contacto">Contacto</Nav.Link>

                <div className={`${styles.navCtas} ms-lg-4 mt-3 mt-lg-0`}>
                  <Button
                    as={Link}
                    target="_blank"
                    to="/login"
                    variant="outline-light"
                    size="sm"
                    className={styles.navSecondaryBtn}
                  >
                    Iniciar sesión
                  </Button>
                </div>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      </header>

      {/* HERO PRINCIPAL */}
      <section className={styles.heroSection} id="home">
        <div className={styles.heroBackgroundGradient} />
        <div className={styles.heroBlurOrbTop} />
        <div className={styles.heroBlurOrbBottom} />

        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <div className={styles.heroBadgeRow}>
                <Badge bg="light" className={styles.heroBadge}>
                  <span className={styles.heroBadgeDot} />
                  Diseñado para firmas de abogados modernas
                </Badge>
              </div>

              <h1 className={styles.heroTitle}>
                Toma el control de tus{" "}
                <span className={styles.heroHighlight}>casos legales</span>
                <br />
                con trazabilidad real.
              </h1>

              <p className={styles.heroSubtitle}>
                NovianCore centraliza toda la información de tus casos de forma
                segura en Microsoft Azure: expedientes, notas, adjuntos y
                cambios de estado quedan organizados en un solo lugar. Olvídate
                del caos de buscar archivos entre carpetas, correos y mensajes.
                Con NovianCore, tu equipo encuentra lo que necesita en segundos,
                con trazabilidad real y control total del historial.
              </p>

              <div className="d-flex flex-wrap gap-3 mt-4">
                <Button
                  as="a"
                  href="https://wa.me/573147712847?text=Hola%20quiero%20recibir%20informaci%C3%B3n%20sobre%20NovianCore%20para%20mi%20firma%20legal"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="lg"
                  className={styles.heroWhatsappBtn}
                >
                  <img
                    src={require("../../assets/images/brand/whatsapp-icon.png")}
                    alt="WhatsApp"
                    className={styles.heroWhatsappIcon}
                  />
                  Agendar demo por WhatsApp
                </Button>
                <Button
                  as={Link}
                  to="/login"
                  target="_blank"
                  variant="outline-light"
                  size="lg"
                  className={styles.heroLoginBtn}
                >
                  Entrar a mi cuenta
                </Button>
              </div>
              {/* Hint debajo de los botones */}
              <p className={styles.heroWhatsappHint}>
                Te respondemos directamente por WhatsApp para entender la
                realidad de tu firma y ayudarte a evaluar si NovianCore encaja
                con tus procesos.
              </p>
              <div className={styles.heroSmallInfo}>
                <div
                  className={`${styles.heroSmallItem} ${styles.heroSmallItemAnimated}`}
                >
                  <CheckCircle2 size={18} className={styles.heroSmallIcon} />
                  <span>Trazabilidad completa por caso</span>
                </div>

                <div
                  className={`${styles.heroSmallItem} ${styles.heroSmallItemAnimated}`}
                >
                  <Users size={18} className={styles.heroSmallIcon} />
                  <span>Gestión de clientes y abogados</span>
                </div>

                <div
                  className={`${styles.heroSmallItem} ${styles.heroSmallItemAnimated}`}
                >
                  <ShieldCheck size={18} className={styles.heroSmallIcon} />
                  <span>Permisos granulares por rol</span>
                </div>

                <div
                  className={`${styles.heroSmallItem} ${styles.heroSmallItemAnimated}`}
                >
                  <Cloud size={18} className={styles.heroSmallIcon} />
                  <span>Seguridad en la nube (Azure)</span>
                </div>

                {/* 🆕 Nuevo ítem — Agendamiento de citas */}
                <div
                  className={`${styles.heroSmallItem} ${styles.heroSmallItemAnimated}`}
                >
                  <Calendar size={18} className={styles.heroSmallIcon} />
                  <span>Agendamiento de citas</span>
                </div>

                {/* 🆕 Nuevo ítem — Recordatorios de casos a vencer */}
                <div
                  className={`${styles.heroSmallItem} ${styles.heroSmallItemAnimated}`}
                >
                  <BellRing size={18} className={styles.heroSmallIcon} />
                  <span>Recordatorios de casos a vencer</span>
                </div>
              </div>
            </Col>

            {/* MOCK DEL TIMELINE / GLASS CARD */}

            <Col lg={6} className="text-center">
              <div className={styles.heroMockWrapper}>
                {/* Floating glass badge - Cerebro Legal */}
                <div className={styles.heroRightFloatingBadge}>
                  <Cpu size={16} className={styles.heroRightFloatingIcon} />
                  <span className={styles.heroRightFloatingText}>
                    Un <b>cerebro legal inteligente</b>
                  </span>
                </div>

                <div className={`${styles.heroCard} shadow-lg`}>
                  <div className={styles.heroCardHeader}>
                    <span className={styles.heroCardTag}>
                      Caso · 2025-000234
                    </span>
                    <span className={styles.heroCardStatus}>En trámite</span>
                  </div>

                  <div className={styles.heroTimelineMock}>
                    <div className={styles.heroTimelineItem}>
                      <div className={styles.heroTimelineDotPrimary} />
                      <div className={styles.heroTimelineIcon}>
                        <FileText size={16} />
                      </div>
                      <div className={styles.heroTimelineContent}>
                        <span className={styles.heroTimelineLabel}>
                          Caso creado
                        </span>
                        <p className={styles.heroTimelineText}>
                          Estado inicial: <b>Registrado</b>. Cliente: Juan
                          Pérez.
                        </p>
                        <span className={styles.heroTimelineMeta}>
                          <Clock size={12} /> 10 ene 2025 · 09:15
                        </span>
                      </div>
                    </div>

                    <div className={styles.heroTimelineItem}>
                      <div className={styles.heroTimelineDot} />
                      <div className={styles.heroTimelineIcon}>
                        <GitBranch size={16} />
                      </div>
                      <div className={styles.heroTimelineContent}>
                        <span className={styles.heroTimelineLabel}>
                          Cambio de estado
                        </span>
                        <p className={styles.heroTimelineText}>
                          De <b>En revisión</b> a{" "}
                          <b>En negociación con la contraparte</b>.
                        </p>
                        <span className={styles.heroTimelineMeta}>
                          <Clock size={12} /> 15 ene 2025 · 16:42
                        </span>
                      </div>
                    </div>

                    <div className={styles.heroTimelineItem}>
                      <div className={styles.heroTimelineDot} />
                      <div className={styles.heroTimelineIcon}>
                        <FileText size={16} />
                      </div>
                      <div className={styles.heroTimelineContent}>
                        <span className={styles.heroTimelineLabel}>
                          Nota interna
                        </span>
                        <p className={styles.heroTimelineText}>
                          “Se presentó memorial de subsanación del radicado
                          2025-000234 ante el Juzgado 12 Civil del Circuito...”
                        </p>
                        <span className={styles.heroTimelineMetaInternal}>
                          Solo uso interno · 2 adjuntos
                        </span>
                      </div>
                    </div>

                    <div className={styles.heroTimelineItemMuted}>
                      <div className={styles.heroTimelineDotMuted} />
                      <div className={styles.heroTimelineIconMuted}>
                        <FileText size={16} />
                      </div>
                      <div className={styles.heroTimelineContent}>
                        <span className={styles.heroTimelineLabel}>
                          Nota eliminada
                        </span>
                        <p className={styles.heroTimelineText}>
                          “Citación del cliente” (1 adjunto eliminado).
                        </p>
                        <span className={styles.heroTimelineMetaMuted}>
                          Acción registrada para auditoría
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.heroCardFooter}>
                    <span>Historial real, sin perder ningún detalle.</span>
                  </div>
                </div>

                <div className={styles.heroFloatingBadge}>
                  <span className={styles.heroFloatingNumber}>100%</span>
                  <span className={styles.heroFloatingText}>
                    de eventos
                    <br />
                    auditables
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* SECCIÓN DE MÉTRICAS / CONFIANZA */}
      <section className={styles.metricsSection}>
        <Container>
          <Row className="text-center">
            <Col md={4} className="mb-4 mb-md-0">
              <div className={styles.metricItem}>
                <span className={styles.metricNumber}>+120</span>
                <span className={styles.metricLabel}>
                  casos gestionados en entorno de prueba
                </span>
              </div>
            </Col>
            <Col md={4} className="mb-4 mb-md-0">
              <div className={styles.metricItem}>
                <span className={styles.metricNumber}>3</span>
                <span className={styles.metricLabel}>
                  capas de trazabilidad: caso, notas y adjuntos
                </span>
              </div>
            </Col>
            <Col md={4}>
              <div className={styles.metricItem}>
                <span className={styles.metricNumber}>24/7</span>
                <span className={styles.metricLabel}>
                  acceso seguro a la información de tus clientes
                </span>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CARACTERÍSTICAS PRINCIPALES */}
      <section className={styles.section} id="caracteristicas">
        <Container>
          <Row className="text-center mb-4">
            <h4 className={styles.sectionEyebrow}>Características</h4>
            <h2 className={styles.sectionTitle}>
              La plataforma integral para firmas legales que buscan orden,
              trazabilidad y eficiencia
            </h2>
            <p className={styles.sectionSubtitle}>
              NovianCore centraliza clientes, abogados, casos, adjuntos y notas
              en un sistema moderno, auditado y seguro en la nube. Todo lo que
              tu firma necesita para operar sin caos.
            </p>
          </Row>

          <Row>
            {/* Gestión de Casos */}
            <Col lg={4} md={6} className="mb-4">
              <Card className={`${styles.featureCard} h-100`}>
                <Card.Body>
                  <div className={styles.featureIconWrapper}>
                    <FolderKanban className={styles.iconPrimary} size={26} />
                  </div>
                  <h5 className="mt-3">Gestión completa de casos</h5>
                  <p className="text-muted mb-0">
                    Manejo de estados, partes, radicados, notas, adjuntos y
                    responsables desde una vista clara y preparada para el
                    trabajo diario.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Timeline */}
            <Col lg={4} md={6} className="mb-4">
              <Card className={`${styles.featureCard} h-100`}>
                <Card.Body>
                  <div className={styles.featureIconWrapper}>
                    <GitBranch className={styles.iconPrimary} size={26} />
                  </div>
                  <h5 className="mt-3">Timeline inteligente</h5>
                  <p className="text-muted mb-0">
                    Historia completa del caso: creaciones, estados, notas,
                    adjuntos subidos o eliminados y auditoría de cada cambio.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Gestión de clientes y abogados */}
            <Col lg={4} md={6} className="mb-4">
              <Card className={`${styles.featureCard} h-100`}>
                <Card.Body>
                  <div className={styles.featureIconWrapper}>
                    <Users className={styles.iconPrimary} size={26} />
                  </div>
                  <h5 className="mt-3">Gestión de clientes y abogados</h5>
                  <p className="text-muted mb-0">
                    Control unificado de clientes, abogados y su relación con
                    los casos.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Notas internas y para cliente */}
            <Col lg={4} md={6} className="mb-4">
              <Card className={`${styles.featureCard} h-100`}>
                <Card.Body>
                  <div className={styles.featureIconWrapper}>
                    <MessageSquareLock
                      className={styles.iconPrimary}
                      size={26}
                    />
                  </div>
                  <h5 className="mt-3">Notas internas y para cliente</h5>
                  <p className="text-muted mb-0">
                    Define fácilmente qué notas son visibles para el cliente y
                    cuáles son exclusivas para tu firma.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Auditoría y Trazabilidad */}
            <Col lg={4} md={6} className="mb-4">
              <Card className={`${styles.featureCard} h-100`}>
                <Card.Body>
                  <div className={styles.featureIconWrapper}>
                    <History className={styles.iconPrimary} size={26} />
                  </div>
                  <h5 className="mt-3">Auditoría completa</h5>
                  <p className="text-muted mb-0">
                    Cada acción queda registrada con usuario, fecha, hora y
                    metadatos. Trazabilidad real para control interno.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Seguridad avanzada */}
            <Col lg={4} md={6} className="mb-4">
              <Card className={`${styles.featureCard} h-100`}>
                <Card.Body>
                  <div className={styles.featureIconWrapper}>
                    <Cloud className={styles.iconPrimary} size={26} />
                  </div>
                  <h5 className="mt-3">Seguridad en la nube</h5>
                  <p className="text-muted mb-0">
                    Cifrado en reposo, adjuntos protegidos, permisos por rol,
                    autenticación con JWT y despliegue en Microsoft Azure.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Recordatorios y vencimientos */}
            <Col lg={4} md={6} className="mb-4">
              <Card className={`${styles.featureCard} h-100`}>
                <Card.Body>
                  <div className={styles.featureIconWrapper}>
                    <CalendarCheck className={styles.iconPrimary} size={26} />
                  </div>
                  <h5 className="mt-3">Recordatorios automáticos</h5>
                  <p className="text-muted mb-0">
                    Agenda tareas, fechas clave y recibe alertas de casos
                    próximos a vencer.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Dashboard y reportes */}
            <Col lg={4} md={6} className="mb-4">
              <Card className={`${styles.featureCard} h-100`}>
                <Card.Body>
                  <div className={styles.featureIconWrapper}>
                    <Presentation className={styles.iconPrimary} size={26} />
                  </div>
                  <h5 className="mt-3">Dashboard e informes</h5>
                  <p className="text-muted mb-0">
                    Métricas claras de casos, estados, carga laboral, notas y
                    adjuntos en un solo panel.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Arquitectura moderna */}
            <Col lg={4} md={6} className="mb-4">
              <Card className={`${styles.featureCard} h-100`}>
                <Card.Body>
                  <div className={styles.featureIconWrapper}>
                    <Layers className={styles.iconPrimary} size={26} />
                  </div>
                  <h5 className="mt-3">Arquitectura moderna</h5>
                  <p className="text-muted mb-0">
                    Clean Architecture, servicios desacoplados, validaciones
                    robustas, API escalable y frontend React pensado para
                    crecer.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FLUJO DE TRABAJO */}
      <section className={styles.sectionAlt} id="flujo">
        <Container>
          <Row className="text-center mb-4 justify-content-center">
            <Col lg={9}>
              <h4 className={styles.sectionEyebrow}>Flujo de trabajo</h4>
              <h2 className={styles.sectionTitle}>
                Un proceso claro y completo para gestionar cada caso
              </h2>
              <p className={styles.sectionSubtitle}>
                NovianCore acompaña el ciclo de vida de cada expediente, desde
                la creación del cliente hasta el cierre del caso, asegurando
                orden, trazabilidad y colaboración en todo momento.
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center">
            {/* 1. Registro del cliente */}
            <Col lg={4} className="mb-4">
              <Card className={`${styles.stepCard} h-100`}>
                <Card.Body>
                  <span className={styles.stepBadge}>1</span>
                  <h5>Registro del cliente</h5>
                  <p className="text-muted mb-0">
                    Crea o vincula un cliente existente y almacena su
                    información de contacto de forma segura, centralizando toda
                    la relación histórica.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* 2. Asignación del abogado */}
            <Col lg={4} className="mb-4">
              <Card className={`${styles.stepCard} h-100`}>
                <Card.Body>
                  <span className={styles.stepBadge}>2</span>
                  <h5>Asignación del abogado</h5>
                  <p className="text-muted mb-0">
                    Define quién llevará el caso y los roles involucrados. Todo
                    queda trazado y visible en el historial.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* 3. Apertura del caso */}
            <Col lg={4} className="mb-4">
              <Card className={`${styles.stepCard} h-100`}>
                <Card.Body>
                  <span className={styles.stepBadge}>3</span>
                  <h5>Apertura del caso</h5>
                  <p className="text-muted mb-0">
                    Registra datos clave: radicado, tipo de trámite, prioridad,
                    estado inicial y partes involucradas.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* 4. Gestión continua */}
            <Col lg={4} className="mb-4">
              <Card className={`${styles.stepCard} h-100`}>
                <Card.Body>
                  <span className={styles.stepBadge}>4</span>
                  <h5>Gestión y seguimiento</h5>
                  <p className="text-muted mb-0">
                    Notas internas y para cliente, adjuntos, cambios de estado,
                    tareas y eventos. Todo queda registrado en el timeline.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* 5. Control de fechas y recordatorios */}
            <Col lg={4} className="mb-4">
              <Card className={`${styles.stepCard} h-100`}>
                <Card.Body>
                  <span className={styles.stepBadge}>5</span>
                  <h5>Fechas clave y alertas</h5>
                  <p className="text-muted mb-0">
                    Gestión de vencimientos, audiencias, plazos, citas y alertas
                    automáticas para evitar retrasos.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* 6. Auditoría y reportes */}
            <Col lg={4} className="mb-4">
              <Card className={`${styles.stepCard} h-100`}>
                <Card.Body>
                  <span className={styles.stepBadge}>6</span>
                  <h5>Auditoría y reportes</h5>
                  <p className="text-muted mb-0">
                    Trazabilidad del 100% de acciones: quién hizo qué, cuándo y
                    cómo. Informes ejecutivos y métricas por caso o por abogado.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* 7. Cierre del caso */}
            <Col lg={4} className="mb-4">
              <Card className={`${styles.stepCard} h-100`}>
                <Card.Body>
                  <span className={styles.stepBadge}>7</span>
                  <h5>Cierre del caso</h5>
                  <p className="text-muted mb-0">
                    Finaliza el expediente con un estado definitivo y conserva
                    todo el historial como respaldo legal.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* 8. Archivo y trazabilidad futura */}
            <Col lg={4} className="mb-4">
              <Card className={`${styles.stepCard} h-100`}>
                <Card.Body>
                  <span className={styles.stepBadge}>8</span>
                  <h5>Archivo y consulta futura</h5>
                  <p className="text-muted mb-0">
                    Toda la información permanece disponible para consultas
                    posteriores: adjuntos, notas, eventos, timelines y
                    documentos.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            {/* 9. Indicadores y mejora continua */}
            <Col lg={4} className="mb-4">
              <Card className={`${styles.stepCard} h-100`}>
                <Card.Body>
                  <span className={styles.stepBadge}>9</span>
                  <h5>Indicadores y mejora continua</h5>
                  <p className="text-muted mb-0">
                    Aprovecha métricas de tiempos, carga operativa, tipos de
                    casos, responsables y estados para optimizar procesos
                    internos y tomar decisiones basadas en datos.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* SEGURIDAD */}
      <section className={styles.sectionAlt} id="seguridad">
        <Container>
          <Row className="text-center mb-4">
            <h4 className={styles.sectionEyebrow}>Seguridad</h4>
            <h2 className={styles.sectionTitle}>
              Pensado para información sensible y confidencial
            </h2>
            <p className={styles.sectionSubtitle}>
              NovianCore está diseñado para proteger expedientes, notas y
              adjuntos de tu firma: permisos granulares por rol, autenticación
              basada en JWT y almacenamiento seguro en la nube de Microsoft
              Azure.
            </p>
          </Row>

          <Row className="justify-content-center">
            <Col lg={10}>
              <Row>
                <Col md={6} className="mb-3">
                  <Card className={`${styles.securityCard} h-100`}>
                    <Card.Body>
                      <ShieldCheck className={styles.iconPrimary} size={24} />
                      <h5 className="mt-3">Permisos granulares por rol</h5>
                      <p className="text-muted mb-0">
                        Definimos permisos a nivel de módulo y acción (por
                        ejemplo: <b>casos.crear</b>, <b>casos.cerrar</b>,
                        <b>clientes.ver</b>). Estos permisos viajan como claims
                        en el JWT y el sistema solo muestra y permite lo que
                        cada usuario está autorizado a hacer.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} className="mb-3">
                  <Card className={`${styles.securityCard} h-100`}>
                    <Card.Body>
                      <ShieldCheck className={styles.iconPrimary} size={24} />
                      <h5 className="mt-3">Autenticación segura con JWT</h5>
                      <p className="text-muted mb-0">
                        El acceso a la plataforma se realiza mediante tokens JWT
                        firmados. Cada petición valida identidad y permisos
                        antes de tocar los datos del caso, reduciendo el riesgo
                        de accesos no autorizados o filtraciones internas.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} className="mb-3">
                  <Card className={`${styles.securityCard} h-100`}>
                    <Card.Body>
                      <ShieldCheck className={styles.iconPrimary} size={24} />
                      <h5 className="mt-3">Datos y adjuntos en Azure</h5>
                      <p className="text-muted mb-0">
                        La información de tu firma vive en servicios de base de
                        datos y almacenamiento de archivos de Microsoft Azure,
                        con cifrado en reposo y comunicación segura vía
                        HTTPS/TLS. Los adjuntos se sirven solo a usuarios
                        autenticados y autorizados.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} className="mb-3">
                  <Card className={`${styles.securityCard} h-100`}>
                    <Card.Body>
                      <ShieldCheck className={styles.iconPrimary} size={24} />
                      <h5 className="mt-3">Multitenencia por empresa</h5>
                      <p className="text-muted mb-0">
                        Cada registro está asociado a un <b>CompanyId</b> y
                        todas las consultas, servicios y validaciones respetan
                        ese contexto. Esto asegura que cada firma vea únicamente
                        su información, incluso cuando la plataforma se comparte
                        entre múltiples clientes.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CONTACTO SIMPLE */}
      <section className={styles.whatsappCtaSection} id="contacto">
        <Container>
          <Row className="text-center mb-4">
            <h4 className={styles.sectionEyebrow}>Contacto</h4>
            <h2 className={styles.sectionTitle}>
              Hablemos sobre lo que tu firma necesita
            </h2>
          </Row>
          <div className={styles.whatsappCtaCard}>
            <div className={styles.whatsappCtaMain}>
              <div className={styles.whatsappCtaIcon}>
                {/* Puedes cambiar el icono si luego agregas uno de WhatsApp como imagen */}
                <span>💬</span>
              </div>
              <div>
                <h3 className={styles.whatsappCtaTitle}>
                  ¿Quieres hablar directamente sobre NovianCore?
                </h3>
                <p className={styles.whatsappCtaText}>
                  Agenda una conversación rápida por WhatsApp y revisemos juntos
                  cómo NovianCore puede ayudar a tu firma a ordenar casos,
                  centralizar la información y mejorar la trazabilidad de cada
                  expediente.
                </p>
              </div>
            </div>

            <div className={styles.whatsappCtaAction}>
              <Button
                as="a"
                href="https://wa.me/573147712847?text=Hola%20quiero%20recibir%20informaci%C3%B3n%20sobre%20NovianCore%20para%20mi%20firma%20legal"
                target="_blank"
                rel="noopener noreferrer"
                variant="light"
                size="lg"
                className={styles.whatsappCtaButton}
              >
                Hablar por WhatsApp
              </Button>
              <span className={styles.whatsappCtaHint}>
                Respuesta directa del desarrollador de la plataforma.
              </span>
            </div>
          </div>
        </Container>
      </section>

      {/* FOOTER */}
      <footer className={styles.footer}>
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="text-center text-md-start mb-2 mb-md-0">
              <small className="">
                © {new Date().getFullYear()} NovianCore. Gestión de casos
                legales para firmas de abogados.
              </small>
            </Col>
            <Col md={6} className="text-center text-md-end">
              <small className="">
                Construido con arquitectura limpia, enfoque en trazabilidad y
                seguridad.
              </small>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default NovianCoreLandingPage;
